// SetupProgress — 5-step onboarding checklist for new-ish orgs (CHG-1238).
//
// Renders only when at least one step is incomplete AND the local dismissal
// flag has not been set (or has expired after 30 days). Each step shows a
// done/incomplete indicator plus a CTA that navigates to the deep-link route
// when present, else dispatches a forward-promised notice.
//
// Per .forge/plans/v2-overview.md.

import * as LucideIcons from "lucide-react";
import { useCallback, useEffect, useState, type ComponentType } from "react";
import { Link } from "react-router-dom";
import type { SetupStep, SetupStepId } from "../../api/overview";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

const DISMISS_KEY = "ag-v2.setup-progress.dismissed";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StepDef {
  id: SetupStepId;
  title: string;
  description: string;
  route: string;
  forwardPromised?: boolean;
  icon: string;
}

// Static per-step UX content. The server tells us done/incomplete; this file
// owns titles + descriptions + routes so the order/wording isn't hard-coded
// in the API response.
//
// CHG-1405 / ITER-3524 — route targets repointed to mounted destinations
// (audit F-02). Settings routes were `/settings/<segment>` paths the router
// doesn't know about; the Settings shell deep-links via `?section=<id>` so
// each route now uses that query-param form. The two `*/new` routes were
// also unmounted and fall back to the list page, whose own emptyState
// already invites the next step (audit V-01). `forwardPromised` is cleared
// now that the targets are real. Spec: dashboard-v2-onboarding-polish-audit.md.
const STEP_DEFS: StepDef[] = [
  {
    id: "payments",
    title: "Connect a payment provider",
    description: "Accept gifts online by linking Stripe or PayPal.",
    route: "/settings?section=integrations",
    icon: "credit-card",
  },
  {
    id: "brand",
    title: "Configure your brand",
    description: "Upload a logo and pick brand colors so donate pages match.",
    route: "/settings?section=brand_identity",
    icon: "palette",
  },
  {
    id: "locale",
    title: "Set localization",
    description: "Choose your default currency, language, and time zone.",
    route: "/settings?section=localization",
    icon: "globe",
  },
  {
    id: "first-designation",
    title: "Create your first designation",
    description: "Designations are where gifts go — funds, programs, projects.",
    route: "/designations",
    icon: "folder-tree",
  },
  {
    id: "first-campaign",
    title: "Launch your first campaign",
    description: "Campaigns track progress against a goal with their own page.",
    route: "/campaigns",
    icon: "megaphone",
  },
];

function readDismissedTs(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const ts = Date.parse(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function isDismissedActive(now: number = Date.now()): boolean {
  const ts = readDismissedTs();
  if (ts === null) return false;
  return now - ts < DISMISS_TTL_MS;
}

export interface SetupProgressProps {
  steps: SetupStep[];
}

export function SetupProgress({ steps }: SetupProgressProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissedActive());

  // Re-check dismissal state when steps change (e.g., user finished a step in
  // another tab) so the card hides if the server now reports all-done. Note:
  // the parent <Overview> already gates on `setup_complete` before rendering;
  // this is belt-and-suspenders.
  useEffect(() => {
    // No-op effect; the dismissed state is read at mount + on dismiss action.
    // Including this useEffect placeholder so future server-pushed setup
    // updates have a clear seam to invalidate.
  }, [steps]);

  const onDismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {
      // ignore storage failures
    }
    setDismissed(true);
  }, []);

  const doneCount = steps.filter((s) => s.done).length;
  const totalCount = steps.length;
  const allDone = doneCount === totalCount && totalCount > 0;

  if (dismissed || allDone) return null;

  const { X, Check, Circle } = LucideIcons as unknown as {
    X: IconCmp;
    Check: IconCmp;
    Circle: IconCmp;
  };

  const stepById = new Map(STEP_DEFS.map((d) => [d.id, d]));

  return (
    <section
      aria-labelledby="setup-progress-title"
      className="card"
      style={{
        padding: 20,
        marginBottom: 20,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        background: "var(--color-primary-tint)",
        borderColor: "var(--color-primary-light)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <h2
            id="setup-progress-title"
            style={{ fontSize: 14, fontWeight: 500, margin: 0, color: "var(--color-text)" }}
          >
            Finish setting up
          </h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>
            {doneCount} of {totalCount} done — you&rsquo;re almost there.
          </div>
        </div>
        <button
          type="button"
          aria-label="Hide setup checklist"
          onClick={onDismiss}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            border: "none",
            background: "transparent",
            color: "var(--color-text-muted)",
            borderRadius: "var(--radius-sm, 6px)",
            cursor: "pointer",
          }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </header>

      <ol
        role="list"
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}
      >
        {steps.map((s) => {
          const def = stepById.get(s.id);
          if (!def) return null;
          return (
            <li key={s.id} role="listitem" data-step-id={s.id} data-step-done={s.done}>
              <Link
                to={def.route}
                aria-current={s.done ? undefined : "step"}
                data-forward-promised={def.forwardPromised ? "true" : undefined}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: 12,
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border-subtle)",
                  borderRadius: "var(--radius-sm, 6px)",
                  textDecoration: "none",
                  color: "var(--color-text)",
                  opacity: s.done ? 0.7 : 1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    marginTop: 2,
                    color: s.done ? "var(--color-success)" : "var(--color-text-muted)",
                  }}
                >
                  {s.done ? <Check size={16} /> : <Circle size={16} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, textDecoration: s.done ? "line-through" : "none" }}>
                    {def.title}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>
                    {def.description}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
